import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Building2, UserCheck, Smartphone, Database, Ban, KeyRound, Target, Globe, Clock, Shield, RefreshCw,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { GET_LEGAL_SETTINGS, LegalSettings } from '../services/queries';

/**
 * Informativa privacy dell'app mobile «UNASCA Studi» (iOS e Android).
 * URL pubblico richiesto da App Store Connect e Google Play: /privacy-app.
 * I dati del Titolare arrivano dalle stesse impostazioni legali della /privacy del sito;
 * i valori di riserva valgono solo se la query non risponde (la pagina la apre anche il
 * revisore degli store, e non deve mai mostrare segnaposto).
 */
const TITOLARE_DI_RISERVA = {
  legalEntityName: 'UNASCA – Unione Nazionale Autoscuole Studi Consulenza Automobilistica',
  legalEntityAddress: 'Viale di Val Fiorita, 88 00144 Roma (RM)',
  legalEntityEmail: 'unasca@unasca.it',
  legalEntityPec: 'unasca@pec.unasca.it',
};

const ULTIMO_AGGIORNAMENTO = '9 ottobre 2026';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
  show?: boolean;
  content: React.ReactNode;
}

const linkClass = 'text-primary-600 dark:text-primary-400 hover:underline';
const th = 'border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold';
const td = 'border border-gray-200 dark:border-gray-600 px-3 py-2 align-top';

const Riga: React.FC<{ cells: React.ReactNode[] }> = ({ cells }) => (
  <tr>{cells.map((c, i) => <td key={i} className={td}>{c}</td>)}</tr>
);

const PrivacyAppPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['1', '4']);
  const { data } = useQuery<{ legalSettings: LegalSettings }>(GET_LEGAL_SETTINGS);
  const legal = data?.legalSettings;

  const nome = legal?.legalEntityName || TITOLARE_DI_RISERVA.legalEntityName;
  const indirizzo = legal?.legalEntityAddress || TITOLARE_DI_RISERVA.legalEntityAddress;
  const email = legal?.legalEntityEmail || TITOLARE_DI_RISERVA.legalEntityEmail;
  const pec = legal?.legalEntityPec || TITOLARE_DI_RISERVA.legalEntityPec;

  const toggleSection = (id: string) => {
    setExpandedSections(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  const sections: Section[] = [
    {
      id: '1',
      title: 'Titolare del trattamento',
      icon: <Building2 className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            L'app <strong>UNASCA Studi</strong> è l'app ufficiale degli associati UNASCA. Titolare del trattamento dei dati
            personali ai sensi del Regolamento (UE) 2016/679 (GDPR) è <strong>{nome}</strong>
            {legal?.legalRepresentative ? <>, in persona del {legal.legalRepresentative}</> : null}, con sede in {indirizzo}.
          </p>
          <ul className="list-none space-y-1">
            {legal?.legalEntityPhone && <li><strong>Tel:</strong> {legal.legalEntityPhone}</li>}
            <li><strong>Email:</strong> <a href={`mailto:${email}`} className={linkClass}>{email}</a></li>
            <li><strong>PEC:</strong> <a href={`mailto:${pec}`} className={linkClass}>{pec}</a></li>
            {legal?.fiscalCode && <li><strong>C.F.:</strong> {legal.fiscalCode}</li>}
          </ul>
          <p>
            L'app è sviluppata, gestita tecnicamente e pubblicata sugli store da <strong>Pluri-Service di Colalongo
            Alessandro</strong>, che opera per conto del Titolare. L'app usa gli stessi sistemi e lo stesso archivio
            dell'area riservata del sito <a href="https://studi.unasca.it" className={linkClass}>studi.unasca.it</a>:
            per tutto ciò che non è specifico dell'app vale l'<Link to="/privacy" className={linkClass}>informativa
            privacy del sito</Link>.
          </p>
        </div>
      ),
    },
    {
      id: '2',
      title: 'Responsabile della Protezione dei Dati (DPO)',
      icon: <UserCheck className="w-5 h-5" />,
      show: !!legal?.dpoName,
      content: (
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <p>Il Responsabile della Protezione dei Dati nominato dal Titolare è <strong>{legal?.dpoName}</strong>.</p>
          <ul className="list-none space-y-1">
            {legal?.dpoEmail && <li><strong>Email:</strong> <a href={`mailto:${legal.dpoEmail}`} className={linkClass}>{legal.dpoEmail}</a></li>}
            {legal?.dpoPhone && <li><strong>Tel:</strong> {legal.dpoPhone}</li>}
          </ul>
        </div>
      ),
    },
    {
      id: '3',
      title: "A chi è rivolta l'app",
      icon: <Smartphone className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            L'app è riservata agli studi di consulenza automobilistica, alle autoscuole e agli studi nautici associati
            UNASCA e ai loro rappresentanti associativi. Una parte è pubblica e non richiede l'accesso: gli articoli del
            blog e la mappa degli studi della Rete Certificata. Usando solo la parte pubblica non viene raccolto alcun
            dato che La identifichi.
          </p>
          <p>L'app non è destinata ai minori e non è possibile creare un account dall'app: l'accesso è riservato a chi è già associato.</p>
        </div>
      ),
    },
    {
      id: '4',
      title: 'Quali dati tratta l\'app, funzione per funzione',
      icon: <Database className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className={th}>Funzione</th>
                  <th className={th}>Dati trattati</th>
                  <th className={th}>Perché</th>
                </tr>
              </thead>
              <tbody>
                <Riga cells={[
                  'Accesso',
                  "Partita IVA dello studio (o email personale, per i rappresentanti); codice monouso (OTP) inviato all'email registrata o, dove attivata, password dell'area riservata.",
                  'Verificare che chi accede sia un associato.',
                ]} />
                <Riga cells={[
                  'Sessione e dispositivi collegati',
                  "Identificativo casuale del dispositivo generato dall'app, nome del modello (es. «Apple iPhone 15»), sistema operativo (iOS o Android), versione dell'app, data dell'ultimo utilizzo. Le credenziali di sessione restano nell'archivio sicuro del telefono (Keychain / Keystore).",
                  'Tenerla collegata senza chiederle ogni volta il codice, mostrarle nel Profilo i telefoni collegati e permetterle di disconnetterli.',
                ]} />
                <Riga cells={[
                  'Notifiche push',
                  'Codice tecnico di notifica del dispositivo (push token), collegato al profilo dello studio o del rappresentante; preferenze di notifica per categoria.',
                  'Inviarle gli avvisi che ha scelto di ricevere (pratiche, messaggi, comunicazioni, scadenze).',
                ]} />
                <Riga cells={[
                  'Mappa «Vicino a me»',
                  'Posizione del telefono, solo quando tocca «Vicino a me» e solo dopo il Suo consenso.',
                  "Centrare la mappa sugli studi vicini. La posizione è usata soltanto sul telefono: non viene inviata ai nostri server né conservata.",
                ]} />
                <Riga cells={[
                  'Allegati',
                  'Foto e documenti che sceglie di allegare (fotocamera o archivio del telefono) a pratiche, richieste di assistenza, discussioni o verbali.',
                  "Svolgere la pratica o la richiesta a cui li allega. L'app accede solo ai file che seleziona Lei.",
                ]} />
                <Riga cells={[
                  'Servizi associativi',
                  'Dati dello studio e del tesseramento, pratiche di svincolo atti e voucher (che possono contenere dati di terzi inseriti dallo studio), messaggi delle pratiche, richieste di assistenza (ticket), discussioni e messaggi della community, risposte ai sondaggi, eventi e comunicazioni dei rappresentanti.',
                  'Erogare i servizi riservati agli associati, gli stessi disponibili nell\'area riservata del sito.',
                ]} />
                <Riga cells={[
                  'Svincolo atti: ricerca degli studi vicini al venditore',
                  "Indirizzo o località digitati per la ricerca, inviati al servizio di ricerca di OpenStreetMap (Nominatim) senza dati che identifichino Lei o lo studio. Nella richiesta di svincolo: targa, prezzo, data dell'appuntamento ed email del venditore.",
                  'Trovare gli studi della Rete Certificata più vicini e gestire la pratica.',
                ]} />
                <Riga cells={[
                  'Pagamenti',
                  "Stato delle quote e ricevute. Il pagamento avviene sul sito dell'associazione tramite Stripe: l'app non riceve né conserva dati di carte o conti.",
                  'Mostrarle cosa ha pagato e cosa resta da pagare.',
                ]} />
                <Riga cells={[
                  'Sblocco con biometria',
                  "Nessun dato biometrico: l'impronta o il volto sono verificati dal sistema operativo, l'app riceve solo l'esito.",
                  'Riaprire l\'app senza reinserire il codice, se lo attiva.',
                ]} />
                <Riga cells={[
                  'Diagnostica degli errori',
                  "Se attiva, l'app potrebbe inviare al servizio Sentry rapporti tecnici di arresto e di errore (tipo di dispositivo, sistema operativo, versione dell'app, schermata, traccia dell'errore, tempi di risposta) collegati al solo identificativo interno dell'associato. Non contengono partita IVA, email, nominativi né contenuti delle schermate.",
                  "Correggere i malfunzionamenti dell'app.",
                ]} />
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: '5',
      title: "Cosa l'app non fa",
      icon: <Ban className="w-5 h-5" />,
      content: (
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-disc pl-5">
          <li>Non mostra pubblicità e non contiene strumenti di profilazione o di tracciamento di terze parti.</li>
          <li>Non usa l'identificativo pubblicitario del dispositivo (IDFA / Advertising ID).</li>
          <li>Non vende né cede dati a terzi per finalità commerciali.</li>
          <li>Non registra la posizione in background e non la conserva.</li>
          <li>Non raccoglie dati di pagamento.</li>
          <li>Non partecipa al backup automatico di Android: i dati della sessione non vengono copiati su altri dispositivi.</li>
        </ul>
      ),
    },
    {
      id: '6',
      title: 'Permessi del dispositivo',
      icon: <KeyRound className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>Ogni permesso è chiesto solo quando serve e può essere negato o revocato dalle impostazioni del telefono; l'app continua a funzionare senza la funzione corrispondente.</p>
          <ul className="space-y-2">
            <li><strong>Notifiche:</strong> per ricevere gli avvisi.</li>
            <li><strong>Posizione (solo mentre usa l'app):</strong> per «Vicino a me» nella mappa.</li>
            <li><strong>Fotocamera e foto:</strong> per fotografare o scegliere un documento da allegare.</li>
            <li><strong>Face ID / impronta:</strong> per lo sblocco rapido, se lo attiva.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '7',
      title: 'Finalità e basi giuridiche',
      icon: <Target className="w-5 h-5" />,
      content: (
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li><strong>Servizi associativi, accesso, sessioni, notifiche, allegati:</strong> esecuzione del rapporto associativo (art. 6.1.b GDPR).</li>
          <li><strong>Posizione per «Vicino a me»:</strong> Sua richiesta e consenso dato dal sistema operativo; il dato resta sul telefono.</li>
          <li><strong>Sicurezza degli accessi e diagnostica degli errori:</strong> legittimo interesse del Titolare al funzionamento sicuro dell'app (art. 6.1.f GDPR).</li>
          <li><strong>Ricevute e dati contabili:</strong> obblighi di legge (art. 6.1.c GDPR).</li>
        </ul>
      ),
    },
    {
      id: '8',
      title: 'Fornitori e trasferimenti',
      icon: <Globe className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className={th}>Servizio</th>
                  <th className={th}>Fornitore</th>
                  <th className={th}>Paese</th>
                </tr>
              </thead>
              <tbody>
                <Riga cells={['Server e database della piattaforma', 'Hostinger (data center di Francoforte)', 'UE']} />
                <Riga cells={['Inoltro delle notifiche push', 'Expo (650 Industries Inc.)', 'USA (CCS)']} />
                <Riga cells={['Recapito delle notifiche', 'Apple (APNs) su iPhone, Google Firebase Cloud Messaging su Android', 'USA (CCS)']} />
                <Riga cells={['Mappa', 'Apple Maps su iPhone, Google Maps su Android', 'USA (CCS)']} />
                <Riga cells={['Ricerca degli indirizzi (svincolo atti)', 'OpenStreetMap Foundation (Nominatim)', 'Regno Unito/UE']} />
                <Riga cells={['Email con il codice di accesso','TurboSMTP (Delivery Tech)', 'Italia/UE']} />
                <Riga cells={['Diagnostica degli errori (se attiva)', 'Sentry (Functional Software Inc.)', 'USA (CCS)']} />
                <Riga cells={['Pagamenti, sul sito', 'Stripe', 'USA (CCS)']} />
              </tbody>
            </table>
          </div>
          <p>
            I dati non sono diffusi. I trasferimenti fuori dall'UE avvengono sulla base di Clausole Contrattuali Standard
            (art. 46.2.c GDPR) o di decisioni di adeguatezza della Commissione europea.
          </p>
        </div>
      ),
    },
    {
      id: '9',
      title: 'Conservazione',
      icon: <Clock className="w-5 h-5" />,
      content: (
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li><strong>Sessione del dispositivo:</strong> scade dopo 365 giorni di inutilizzo, oppure quando esce dall'app o la disconnette da un altro telefono; le sessioni chiuse sono cancellate automaticamente alla scadenza.</li>
          <li><strong>Codice di notifica:</strong> fino all'uscita dall'app, alla disconnessione del dispositivo o a quando il servizio di notifica lo segnala come non più valido.</li>
          <li><strong>Posizione:</strong> mai conservata.</li>
          <li><strong>Dati sul telefono:</strong> credenziali e copia locale dei dati già visualizzati restano sul telefono per farlo funzionare anche senza rete; uscendo dall'app le credenziali e la copia dei dati vengono cancellate (restano solo preferenze locali, come i membri della community che ha bloccato), disinstallandola si cancella tutto.</li>
          <li><strong>Rapporti di errore:</strong> secondo il periodo di conservazione del servizio di diagnostica, al massimo 90 giorni.</li>
          <li><strong>Pratiche, messaggi, ticket, community, sondaggi, ricevute:</strong> come per l'area riservata del sito, secondo l'<Link to="/privacy" className={linkClass}>informativa del sito</Link>.</li>
        </ul>
      ),
    },
    {
      id: '10',
      title: "Diritti dell'interessato e contatti",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Può esercitare in ogni momento i diritti degli artt. 15-22 del GDPR (accesso, rettifica, cancellazione,
            limitazione, portabilità, opposizione, revoca del consenso) scrivendo a{' '}
            <a href={`mailto:${email}`} className={linkClass}>{email}</a> o via PEC a{' '}
            <a href={`mailto:${pec}`} className={linkClass}>{pec}</a>
            {legal?.dpoEmail ? <> oppure al DPO, <a href={`mailto:${legal.dpoEmail}`} className={linkClass}>{legal.dpoEmail}</a></> : null}.
            Ha inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali
            (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className={linkClass}>www.garanteprivacy.it</a>).
          </p>
          <p>
            Può revocare subito i permessi dalle impostazioni del telefono, disattivare le notifiche per categoria dal
            Profilo dell'app, disconnettere i dispositivi collegati dal Profilo e chiedere la cancellazione dei dati
            agli indirizzi sopra. Per assistenza sull'app: <Link to="/contact" className={linkClass}>pagina Contatti</Link>.
          </p>
        </div>
      ),
    },
    {
      id: '11',
      title: "Modifiche all'informativa",
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Il Titolare può aggiornare questa informativa, per esempio quando l'app acquisisce nuove funzioni. Le modifiche
          sono pubblicate su questa pagina, con la data dell'ultimo aggiornamento in fondo.
        </p>
      ),
    },
  ];

  const visibleSections = sections.filter(s => s.show !== false);

  return (
    <div className="container-custom py-8 legal-content">
      <Helmet>
        <title>Privacy dell'app UNASCA Studi | UNASCA</title>
        <meta name="description" content="Informativa sul trattamento dei dati personali dell'app mobile UNASCA Studi per iOS e Android." />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl mb-4">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white mb-2">Informativa Privacy dell'app UNASCA Studi</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Informativa sul trattamento dei dati personali dell'app per iOS e Android, ai sensi dell'art. 13 del Regolamento (UE) 2016/679 (GDPR)
          </p>
        </div>

        <div className="space-y-4">
          {visibleSections.map((section, index) => (
            <div
              key={section.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                aria-expanded={expandedSections.includes(section.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-indigo-600 dark:text-indigo-400">{section.icon}</div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{index + 1}. {section.title}</h2>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.includes(section.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                  <div className="pt-4">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
          <p>Ultimo aggiornamento: {ULTIMO_AGGIORNAMENTO}</p>
          <p className="mt-1">Riferimenti normativi: Regolamento (UE) 2016/679 (GDPR) · D.Lgs. 196/2003 · D.Lgs. 101/2018</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyAppPage;
